import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import { ArrowRight, Leaf, Ban, Trophy, Package } from 'lucide-react';
import { API_URL, resolveImageUrl } from '../config';
import './HeroSlider.css';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const defaultSlides = [
  {
    id: 1,
    title: "Pure Himalayan Makhana",
    subtitle: "DIRECT FROM THE SOURCE",
    description: "Superfood harvested directly from pristine Himalayan water bodies. Loaded with protein, calcium, and essential nutrients.",
    offer: "15%",
    productImage: "https://images.unsplash.com/photo-1596547609652-9cf5d8d76921?auto=format&fit=crop&q=80&w=600",
    bgImage: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=1600"
  },
  {
    id: 2,
    title: "California Almonds (Bold)",
    subtitle: "HANDPICKED & SORTED",
    description: "Extra-large, crunchy almonds rich in Vitamin E, dietary fiber, and healthy fats. Your perfect daily morning snack.",
    offer: "20%",
    productImage: "https://images.unsplash.com/photo-1508061253366-f7da158b6d46?auto=format&fit=crop&q=80&w=600",
    bgImage: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&q=80&w=1600"
  },
  {
    id: 3,
    title: "Snow-White Kashmiri Walnuts",
    subtitle: "ORGANIC BRAIN FOOD",
    description: "Premium quality paper-shell walnuts from the valleys of Kashmir. Rich in Omega-3 fatty acids and antioxidants.",
    offer: "10%",
    productImage: "https://images.unsplash.com/photo-1601004890684-d8cbf643f5f2?auto=format&fit=crop&q=80&w=600",
    bgImage: "https://images.unsplash.com/photo-1454496522488-7a8e488e8606?auto=format&fit=crop&q=80&w=1600"
  },
  {
    id: 4,
    title: "Premium Whole Cashews",
    subtitle: "DELICIOUSLY CREAMY",
    description: "Crispy, naturally sweet whole cashew kernels. Hygienically packed to retain their natural oils and fresh taste.",
    offer: "25%",
    productImage: "https://images.unsplash.com/photo-1600002415506-dd0609260c18?auto=format&fit=crop&q=80&w=600",
    bgImage: "https://images.unsplash.com/photo-1486873249359-2731bd6dafc7?auto=format&fit=crop&q=80&w=1600"
  }
];




const HeroSlider = () => {
  const [slides, setSlides] = useState(defaultSlides);

  useEffect(() => {
    fetch(`${API_URL}/settings/hero_slider`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load slides');
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setSlides(data);
        }
      })
      .catch((err) => console.error('Error fetching hero_slider settings:', err));
  }, []);

  return (
    <section className="hero-slider-section" aria-label="Featured Products Slider">
      <Swiper
        modules={[Autoplay, EffectFade, Pagination]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        loop={true}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
        }}
        pagination={{
          clickable: true,
          el: '.swiper-pagination-custom',
        }}
        className="mySwiper"
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.id}>
            <div className="hero-slide">
              {/* Blurred Background image */}
              <div 
                className="hero-slide-bg" 
                style={{ backgroundImage: `url(${resolveImageUrl(slide.bgImage)})` }}
              />
              <div className="hero-slide-overlay" />
              
              {/* Slide Content */}
              <div className="container hero-slide-content">
                {/* Left Side */}
                <div className="hero-left-col">
                  <span className="hero-subtitle">{slide.subtitle}</span>
                  <h1 className="hero-heading">{slide.title}</h1>
                  <p className="hero-desc">{slide.description}</p>
                  
                  {/* Trust Badges */}
                  <div className="hero-badges-wrapper">
                    <div className="hero-badge-tag">
                      <Leaf size={14} className="badge-icon" />
                      <span>100% Natural</span>
                    </div>
                    <div className="hero-badge-tag">
                      <Ban size={14} className="badge-icon" />
                      <span>No Preservatives</span>
                    </div>
                    <div className="hero-badge-tag">
                      <Trophy size={14} className="badge-icon" />
                      <span>Premium Quality</span>
                    </div>
                    <div className="hero-badge-tag">
                      <Package size={14} className="badge-icon" />
                      <span>Hygienically Packed</span>
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  <a href="#best-sellers" className="hero-cta-btn">
                    <span>Shop Now</span>
                    <ArrowRight size={18} />
                  </a>
                </div>

                {/* Right Side */}
                <div className="hero-right-col">
                  <div className="hero-product-container">
                    <div className="hero-product-img-wrapper">
                      <img 
                        src={resolveImageUrl(slide.productImage)} 
                        alt={slide.title} 
                        loading="lazy" 
                      />
                    </div>
                    {/* Offer Badge */}
                    <div className="hero-offer-badge">
                      <span className="text-up">UP TO</span>
                      <span className="text-percent">{slide.offer}</span>
                      <span className="text-off">OFF</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
        {/* Custom Pagination at bottom center */}
        <div className="swiper-pagination-custom" />
      </Swiper>
    </section>
  );
};

export default HeroSlider;
